import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import LanguagePairList from "./LanguagePairList";
import LanguagePairForm from "./LanguagePairForm";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";

const LanguagePairsPage = () => {
  const [pairs, setPairs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pairToDelete, setPairToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pairsRes, languagesRes] = await Promise.all([
          api.get("/admin-language-pairs"),
          api.get("/languages"),
        ]);
        setPairs(pairsRes.data.data);
        setLanguages(languagesRes.data);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Could not load required data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        await api.put(`/admin-language-pairs/${editId}`, formData);
      } else {
        await api.post("/admin-language-pairs", formData);
      }

      const res = await api.get("/admin-language-pairs");
      setPairs(res.data.data);
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save language pair");
    }
  };

  const handleDelete = async () => {
    if (!pairToDelete) return;
    try {
      await api.delete(`/admin-language-pairs/${pairToDelete}`);
      setPairs(pairs.filter((p) => p.id !== pairToDelete));
      setPairToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete language pair");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingPair =
    action === "edit" && editId
      ? pairs.find((p) => String(p.id) === String(editId))
      : null;

  return (
    <>
      <div className="md:w-1/2">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Language Pairs</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Language Pair
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <LanguagePairForm
            itemToEdit={editingPair}
            onSave={handleSave}
            onCancel={() => setAction(null)}
            languages={languages}
          />
        ) : (
          <LanguagePairList
            pairs={pairs}
            onEdit={(pair) => setAction("edit", pair.id)}
            onDelete={(id) => setPairToDelete(id)}
          />
        )}
      </div>

      {pairToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this language pair? This action cannot be undone."
          onCancel={() => setPairToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default LanguagePairsPage;
